import React, { useEffect } from "react";
import { FooterType } from "../../features/footer/footerTypes";
import { fetchFooter } from "../../features/footer/footerTypeActions";
import { ApplicationState } from "../../redux/initialState";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Markdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import { useTheme } from "@emotion/react";
import { useBreakpoint } from "@tal-gel/components-v2";
import { Caption, CaptionCondensed, Icon, Link, Paragraph } from "@tal-gel/components-v2";
import { ColumnFlexBox, RowFlexBox } from "../common/FlexBoxes";
import styled from "@emotion/styled";

interface PropsFromState {
  loading: boolean;
  data: FooterType;
  errors?: string;
}

interface propsFromDispatch {
  fetchFooter: () => any;
}

type AllProps = PropsFromState & propsFromDispatch;

function isEmptyObject(obj: any) {
  return Object.keys(obj).length === 0;
}

const FooterWrapper = styled.div`
  padding: 32px 18px;
`;

const StyledRowFlexBox = styled(RowFlexBox)<{ ismd: boolean }>`
  max-width: 1080px;
  flex-direction: ${({ ismd }) => (ismd ? "row" : "column")};
  align-items: ${({ ismd }) => (ismd ? "flex-start" : "center")};
  gap: 32px;
`;

const StyledColumnFlexBox = styled(ColumnFlexBox)`
  gap: 16px;
`;

const StyledIcon = styled(Icon)`
  padding-bottom: 8px;
`;

const Footer: React.FC<AllProps> = ({ data, fetchFooter }) => {
  useEffect(() => {
    if (isEmptyObject(data)) {
      fetchFooter();
    }
  }, [data, fetchFooter]);

  const theme = useTheme();
  const isMD = useBreakpoint("md");

  return (
    <FooterWrapper>
      <StyledRowFlexBox ismd={isMD}>
        <a href={data.footerLogoUrl} rel={"noopener noreferrer"} target="_blank">
          <img src={data.footerLogo} width="150px" height="auto" alt={"TAL Logo"} />
        </a>
        <StyledColumnFlexBox>
          <Paragraph appearance="inverse">{data.footerDescription}</Paragraph>
          <Caption appearance="inverse">
            <Markdown
              rehypePlugins={[rehypeSanitize]}
              components={{
                p(props: any) {
                  return (
                    <CaptionCondensed style={{ color: theme.tokens.global.themeColorBackgroundInverseOverlayDark }}>
                      {props?.children}
                    </CaptionCondensed>
                  );
                },
                a(props: any) {
                  return (
                    <Link rel={"noopener noreferrer"} target="_blank" href={props?.href} appearance="inverse">
                      {props?.children}
                      <StyledIcon name="LinkExternal" variant="inline" width={"24px"} fill={theme.tokens.global.themeColorIconDefault} />
                    </Link>
                  );
                },
              }}
            >
              {data?.footerCaption}
            </Markdown>
          </Caption>
        </StyledColumnFlexBox>
      </StyledRowFlexBox>
    </FooterWrapper>
  );
};

const mapStateToProps = ({ footerType }: ApplicationState) => ({
  loading: footerType.loading,
  errors: footerType.errors,
  data: footerType.data,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    fetchFooter: () => {
      dispatch(fetchFooter());
    },
  };
};

Footer.propTypes = {
  loading: PropTypes.any,
  errors: PropTypes.string,
  data: PropTypes.any.isRequired,
  fetchFooter: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
